/**
 * Notifications Component - SSE-based real-time notifications
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstance } from '../../contexts/InstanceContext';
import { Bell, X, Check, AlertTriangle, ShoppingCart, Package, DollarSign } from 'lucide-react';
import { Button } from './button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { Badge } from './badge';
import { ScrollArea } from './scroll-area';
import { toast } from 'sonner';
import './NotificationCenter.css';

const NOTIFICATION_ICONS = {
  sale_completed: DollarSign,
  cashier_alert: AlertTriangle,
  low_stock: Package,
  order_created: ShoppingCart,
  system_message: Bell,
  connected: Check,
};

const NOTIFICATION_COLORS = {
  sale_completed: 'text-emerald-500',
  cashier_alert: 'text-amber-500',
  low_stock: 'text-red-500',
  order_created: 'text-blue-500',
  system_message: 'text-muted-foreground',
  connected: 'text-emerald-500',
};

export function NotificationCenter() {
  const { user, token } = useAuth();
  const { formatCurrency } = useInstance();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connectSSE = useCallback(() => {
    if (!token || eventSourceRef.current) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const sseUrl = `${backendUrl}/api/notifications/stream`;

    try {
      // Create EventSource with token in query (SSE doesn't support headers)
      const eventSource = new EventSource(sseUrl, {
        withCredentials: false,
      });

      // For SSE with auth, we need to use a custom approach
      // Since EventSource doesn't support custom headers, we'll use fetch-event-source pattern
      // For now, we'll use a simpler polling fallback if SSE fails
      
      eventSource.onopen = () => {
        console.log('SSE Connected');
        setIsConnected(true);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'heartbeat') {
            return; // Ignore heartbeats
          }
          
          if (data.type === 'connected') {
            toast.success('Notificações ativadas');
            return;
          }
          
          // Add notification
          const notification = {
            id: Date.now().toString(),
            ...data,
            read: false,
          };
          
          setNotifications(prev => [notification, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + 1);
          
          // Show toast for important notifications
          if (['sale_completed', 'cashier_alert', 'low_stock'].includes(data.type)) {
            toast(data.title, {
              description: data.message,
              icon: data.type === 'sale_completed' ? '💰' : 
                    data.type === 'cashier_alert' ? '⚠️' : '📦',
            });
          }
        } catch (err) {
          console.error('Error parsing SSE message:', err);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE Error:', error);
        setIsConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSSE();
        }, 5000);
      };

      eventSourceRef.current = eventSource;
    } catch (err) {
      console.error('Error creating EventSource:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      connectSSE();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, connectSSE]);

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Agora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative notification-trigger"
          data-testid="notification-btn"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {isConnected && (
            <span className="absolute bottom-0 right-0 w-2 h-2 bg-emerald-500 rounded-full" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 notification-popover" 
        align="end"
        data-testid="notification-panel"
      >
        <div className="notification-header">
          <h4 className="font-semibold">Notificações</h4>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                <Check className="h-4 w-4 mr-1" />
                Ler todas
              </Button>
            )}
            {notifications.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="notification-empty">
              <Bell className="h-8 w-8 text-muted-foreground mb-2" />
              <p>Nenhuma notificação</p>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map(notification => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                const colorClass = NOTIFICATION_COLORS[notification.type] || '';
                
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.read ? 'unread' : ''}`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className={`notification-icon ${colorClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="notification-content">
                      <p className="notification-title">{notification.title}</p>
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">
                        {formatTime(notification.timestamp)}
                      </span>
                    </div>
                    {!notification.read && (
                      <div className="notification-unread-dot" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationCenter;
